/* ── Expense Tracking — Types & Mock Data ── */

export type ExpenseStatus = 'paid' | 'pending' | 'rejected' | 'void';
export type ExpensePaymentMethod = 'cash' | 'cheque' | 'bank_transfer';

export interface ExpenseCategory {
    id: string;
    name: string;
    description: string;
    allocated_budget: number | null;
    is_active: boolean;
}

export interface Expense {
    id: string;
    date: string;
    voucher_number: string;
    payee: string;
    category_id: string;
    category_name: string;
    description: string;
    amount: number;
    payment_method: ExpensePaymentMethod;
    reference_number: string;
    status: ExpenseStatus;
    has_attachment: boolean;
    created_at: string;
}

/* ── Mock categories ── */
export const mockCategories: ExpenseCategory[] = [
    { id: 'ec1', name: 'Utilities', description: 'Electricity, water, internet', allocated_budget: 50000, is_active: true },
    { id: 'ec2', name: 'Academic Supplies', description: 'Books, stationery, lab materials', allocated_budget: 80000, is_active: true },
    { id: 'ec3', name: 'Maintenance', description: 'Building repairs, plumbing, electrical', allocated_budget: 100000, is_active: true },
    { id: 'ec4', name: 'Transport Fuel', description: 'Diesel, petrol for school buses', allocated_budget: 60000, is_active: true },
    { id: 'ec5', name: 'Staff Welfare', description: 'Tea, snacks, medical aid', allocated_budget: 25000, is_active: true },
    { id: 'ec6', name: 'Events & Functions', description: 'Annual day, sports day, guests', allocated_budget: 40000, is_active: true },
    { id: 'ec7', name: 'Printing & Copying', description: 'Examination papers, notices', allocated_budget: 15000, is_active: false },
];

/* ── Mock expenses ── */
export const mockExpenses: Expense[] = [
    { id: 'e1', date: '2026-02-28', voucher_number: 'VCH-2026-001', payee: 'City Power Corp.', category_id: 'ec1', category_name: 'Utilities', description: 'February electricity bill', amount: 12500, payment_method: 'bank_transfer', reference_number: 'TRF-88201', status: 'paid', has_attachment: true, created_at: '2026-02-28T10:00:00Z' },
    { id: 'e2', date: '2026-02-26', voucher_number: 'VCH-2026-002', payee: 'Sharma Book House', category_id: 'ec2', category_name: 'Academic Supplies', description: 'Grade 5-8 textbooks (Q4)', amount: 34200, payment_method: 'cheque', reference_number: 'CHQ-556677', status: 'paid', has_attachment: true, created_at: '2026-02-26T09:30:00Z' },
    { id: 'e3', date: '2026-02-25', voucher_number: 'VCH-2026-003', payee: 'Kumar Electricals', category_id: 'ec3', category_name: 'Maintenance', description: 'Wiring repair — Block C', amount: 8750, payment_method: 'cash', reference_number: '', status: 'paid', has_attachment: false, created_at: '2026-02-25T14:15:00Z' },
    { id: 'e4', date: '2026-02-24', voucher_number: 'VCH-2026-004', payee: 'Indian Oil Depot', category_id: 'ec4', category_name: 'Transport Fuel', description: 'Bus fleet diesel — Feb 2nd half', amount: 22000, payment_method: 'bank_transfer', reference_number: 'TRF-88305', status: 'pending', has_attachment: true, created_at: '2026-02-24T11:00:00Z' },
    { id: 'e5', date: '2026-02-22', voucher_number: 'VCH-2026-005', payee: 'Metro Water Supply', category_id: 'ec1', category_name: 'Utilities', description: 'February water bill', amount: 4500, payment_method: 'bank_transfer', reference_number: 'TRF-88410', status: 'paid', has_attachment: false, created_at: '2026-02-22T16:00:00Z' },
    { id: 'e6', date: '2026-02-20', voucher_number: 'VCH-2026-006', payee: 'ABC Caterers', category_id: 'ec5', category_name: 'Staff Welfare', description: 'Monthly staff tea & snacks', amount: 6800, payment_method: 'cash', reference_number: '', status: 'paid', has_attachment: false, created_at: '2026-02-20T12:00:00Z' },
    { id: 'e7', date: '2026-02-18', voucher_number: 'VCH-2026-007', payee: 'Patel Hardware', category_id: 'ec3', category_name: 'Maintenance', description: 'Plumbing fix — washrooms', amount: 3200, payment_method: 'cash', reference_number: '', status: 'rejected', has_attachment: false, created_at: '2026-02-18T15:00:00Z' },
    { id: 'e8', date: '2026-02-15', voucher_number: 'VCH-2026-008', payee: 'Quick Print Services', category_id: 'ec7', category_name: 'Printing & Copying', description: 'Unit test papers printing', amount: 2800, payment_method: 'cash', reference_number: '', status: 'paid', has_attachment: true, created_at: '2026-02-15T09:00:00Z' },
    { id: 'e9', date: '2026-02-12', voucher_number: 'VCH-2026-009', payee: 'Sunrise Events', category_id: 'ec6', category_name: 'Events & Functions', description: 'Annual day stage decoration advance', amount: 15000, payment_method: 'cheque', reference_number: 'CHQ-556790', status: 'pending', has_attachment: true, created_at: '2026-02-12T10:30:00Z' },
    { id: 'e10', date: '2026-02-10', voucher_number: 'VCH-2026-010', payee: 'Lab Equipment Co.', category_id: 'ec2', category_name: 'Academic Supplies', description: 'Physics lab instruments', amount: 18500, payment_method: 'bank_transfer', reference_number: 'TRF-88100', status: 'paid', has_attachment: true, created_at: '2026-02-10T13:00:00Z' },
];

/* ── Helpers ── */
const delay = (ms: number) => new Promise((r) => setTimeout(r, ms));

export async function fetchCategories(): Promise<ExpenseCategory[]> {
    await delay(300);
    return mockCategories;
}

export async function fetchExpenses(): Promise<Expense[]> {
    await delay(400);
    return mockExpenses;
}

const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine',
    'Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];

function twoDigit(n: number): string {
    if (n < 20) return ones[n];
    return tens[Math.floor(n / 10)] + (n % 10 ? ' ' + ones[n % 10] : '');
}

export function numberToWords(num: number): string {
    if (num === 0) return 'Zero';
    const n = Math.floor(Math.abs(num));
    const paise = Math.round((Math.abs(num) - n) * 100);

    let words = '';
    if (n >= 10000000) words += twoDigit(Math.floor(n / 10000000)) + ' Crore ';
    if (n >= 100000) words += twoDigit(Math.floor((n % 10000000) / 100000)) + ' Lakh ';
    if (n >= 1000) words += twoDigit(Math.floor((n % 100000) / 1000)) + ' Thousand ';
    if (n >= 100) words += ones[Math.floor((n % 1000) / 100)] + ' Hundred ';
    const rem = n % 100;
    if (rem) words += (words ? 'and ' : '') + twoDigit(rem) + ' ';

    words = 'Rupees ' + words.trim();
    if (paise > 0) words += ' and ' + twoDigit(paise) + ' Paise';
    words += ' Only';
    return words;
}
